import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { firebaseAuth, firebaseDb } from '../firebase';
import { config } from '../utils/config';

/** Même format que l’ancien DeliveryService côté serveur (MF-…). */
function generateTrackingId() {
  const prefix = 'MF';
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `${prefix}-${timestamp}-${random}`;
}

export const orderService = {
  /**
   * Créer une nouvelle commande
   */
  createOrder: async (orderData) => {
    try {
      if (!firebaseAuth?.currentUser) {
        return { success: false, error: 'Non authentifié' };
      }
      if (!firebaseDb) {
        return { success: false, error: 'Firestore non configuré' };
      }
      const { userId: _omitUserId, skipScraping: _omitSkip, ...cleanOrder } = orderData;
      const payload = {
        ...cleanOrder,
        userUid: firebaseAuth.currentUser.uid,
        orderType: cleanOrder.orderType || 'international',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      // Valeurs par défaut (compat UI)
      payload.status = payload.status || 'Commandée';
      payload.paymentStatus = payload.paymentStatus || 'En attente';
      payload.deliveryStatus = payload.deliveryStatus || 'Non expédié';

      const ref = await addDoc(collection(firebaseDb, 'orders'), payload);
      const snap = await getDoc(ref);
      const order = { id: ref.id, _id: ref.id, ...snap.data() };
      return {
        success: true,
        order,
      };
    } catch (error) {
      console.error('Erreur lors de la création de la commande:', error);
      return {
        success: false,
        error: error.message || 'Erreur lors de la création de la commande',
      };
    }
  },

  /**
   * Commande boutique locale — prix connu, stock décrémenté.
   * @param {string} [paymentMethod] 'online' | 'cash_on_delivery'
   */
  createLocalOrder: async ({
    cartItems,
    deliveryAddress = null,
    deliveryFee = null,
    paymentMethod = 'online',
  }) => {
    try {
      if (!firebaseAuth?.currentUser || !firebaseDb) {
        return { success: false, error: 'Non authentifié' };
      }
      if (!cartItems?.length) {
        return { success: false, error: 'Panier vide' };
      }

      const isCod = paymentMethod === 'cash_on_delivery';
      const fee = deliveryFee ?? config.LOCAL_DELIVERY_FEE;
      let subtotal = 0;
      const orderItems = [];

      const orderRef = doc(collection(firebaseDb, 'orders'));

      await runTransaction(firebaseDb, async (tx) => {
        for (const line of cartItems) {
          const productRef = doc(firebaseDb, 'products', line.productId);
          const productSnap = await tx.get(productRef);
          if (!productSnap.exists()) {
            throw new Error(`Produit introuvable : ${line.name}`);
          }
          const product = productSnap.data();
          if (!product.isActive) {
            throw new Error(`Produit indisponible : ${line.name}`);
          }
          const stock = Number(product.stock) || 0;
          if (stock < line.quantity) {
            throw new Error(`Stock insuffisant pour « ${line.name} » (${stock} restant)`);
          }

          const unitPrice = Number(product.price);
          const lineTotal = unitPrice * line.quantity;
          subtotal += lineTotal;

          tx.update(productRef, {
            stock: stock - line.quantity,
            updatedAt: serverTimestamp(),
          });

          orderItems.push({
            productId: line.productId,
            productName: product.name,
            imageUrl: line.thumbnailUrl || product.thumbnailUrl || product.images?.[0] || '',
            unitPrice,
            productPrice: lineTotal,
            quantity: line.quantity,
            size: line.size || '',
            color: line.color || '',
          });
        }

        const totalAmount = Math.round((subtotal + fee) * 100) / 100;

        tx.set(orderRef, {
          userUid: firebaseAuth.currentUser.uid,
          orderType: 'local',
          platform: 'Boutique Maxyfly',
          paymentMethod: isCod ? 'cash_on_delivery' : 'online',
          items: orderItems,
          subtotal,
          deliveryFee: fee,
          totalAmount,
          finalAmount: totalAmount,
          totalProductPrice: subtotal,
          deliveryAddress: deliveryAddress || null,
          comment: '',
          status: 'Commandée',
          paymentStatus: isCod ? 'À la livraison' : 'En attente',
          deliveryStatus: 'Non expédié',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });

      const snap = await getDoc(orderRef);
      return {
        success: true,
        order: { id: orderRef.id, _id: orderRef.id, ...snap.data() },
      };
    } catch (error) {
      console.error('createLocalOrder:', error);
      return { success: false, error: error.message || 'Erreur commande locale' };
    }
  },

  /**
   * Obtenir toutes les commandes d'un utilisateur
   */
  getUserOrders: async (userId) => {
    try {
      if (!firebaseAuth?.currentUser) {
        return { success: false, error: 'Non authentifié', orders: [] };
      }
      if (!firebaseDb) {
        return { success: false, error: 'Firestore non configuré', orders: [] };
      }
      const uid = userId || firebaseAuth.currentUser.uid;
      const q = query(
        collection(firebaseDb, 'orders'),
        where('userUid', '==', uid),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      const orders = snap.docs.map((d) => ({ id: d.id, _id: d.id, ...d.data() }));
      return {
        success: true,
        orders,
        count: orders.length,
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes:', error);
      return {
        success: false,
        error: error.message || 'Erreur lors de la récupération des commandes',
        orders: [],
      };
    }
  },

  /**
   * Obtenir une commande par ID
   */
  getOrderById: async (orderId) => {
    try {
      if (!firebaseDb) {
        return { success: false, error: 'Firestore non configuré' };
      }
      const ref = doc(firebaseDb, 'orders', orderId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        return { success: false, error: 'Commande introuvable' };
      }
      return {
        success: true,
        order: { id: snap.id, _id: snap.id, ...snap.data() },
      };
    } catch (error) {
      console.error('Erreur lors de la récupération de la commande:', error);
      return {
        success: false,
        error: error.message || 'Erreur lors de la récupération de la commande',
      };
    }
  },

  /**
   * Mettre à jour le statut d'une commande
   */
  updateOrderStatus: async (orderId, updates) => {
    try {
      if (!firebaseDb) {
        return { success: false, error: 'Firestore non configuré' };
      }
      const ref = doc(firebaseDb, 'orders', orderId);
      await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });
      const snap = await getDoc(ref);
      return {
        success: true,
        order: { id: snap.id, _id: snap.id, ...snap.data() },
      };
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la commande:', error);
      return {
        success: false,
        error: error.message || 'Erreur lors de la mise à jour de la commande',
      };
    }
  },

  /**
   * Supprimer une commande
   */
  deleteOrder: async (orderId) => {
    try {
      if (!firebaseDb) {
        return { success: false, error: 'Firestore non configuré' };
      }
      await deleteDoc(doc(firebaseDb, 'orders', orderId));
      return {
        success: true,
        order: { id: orderId, _id: orderId },
      };
    } catch (error) {
      console.error('Erreur lors de la suppression de la commande:', error);
      return {
        success: false,
        error: error.message || 'Erreur lors de la suppression de la commande',
      };
    }
  },

  /**
   * Obtenir toutes les commandes (pour admin/agent)
   */
  getAllOrders: async (filters = {}) => {
    try {
      if (!firebaseDb) {
        return { success: false, error: 'Firestore non configuré', orders: [] };
      }
      let q = query(collection(firebaseDb, 'orders'), orderBy('createdAt', 'desc'));
      if (filters.status) q = query(collection(firebaseDb, 'orders'), where('status', '==', filters.status), orderBy('createdAt', 'desc'));
      return {
        success: true,
        orders: (await getDocs(q)).docs.map((d) => ({ id: d.id, _id: d.id, ...d.data() })),
        count: (await getDocs(q)).size,
      };
    } catch (error) {
      console.error('Erreur lors de la récupération de toutes les commandes:', error);
      return {
        success: false,
        error: error.message || 'Erreur lors de la récupération des commandes',
        orders: [],
      };
    }
  },

  /**
   * Mettre à jour le prix d'une commande (pour agent/admin).
   * Calcule totalAmount / détails pour que le client voie « Payer maintenant » (Dashboard/Payment).
   */
  updateOrderPrice: async (orderId, priceData) => {
    try {
      if (!firebaseDb) {
        return { success: false, error: 'Firestore non configuré' };
      }
      const ref = doc(firebaseDb, 'orders', orderId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        return { success: false, error: 'Commande introuvable' };
      }
      const order = snap.data();

      const rate =
        priceData.commissionRate != null && priceData.commissionRate !== ''
          ? parseFloat(priceData.commissionRate)
          : 0.15;

      const hasMulti =
        Array.isArray(priceData.items) &&
        priceData.items.length > 0 &&
        Array.isArray(order.items) &&
        order.items.length > 0;

      let updates = {
        commissionRate: rate,
        updatedAt: serverTimestamp(),
      };

      const paymentStatusKeepPaid = order.paymentStatus === 'Payé';
      const nextPaymentStatus = paymentStatusKeepPaid ? 'Payé' : 'En attente';

      if (hasMulti) {
        const mergedItems = order.items.map((it, i) => ({
          ...it,
          productPrice:
            priceData.items[i] != null && priceData.items[i].productPrice != null
              ? parseFloat(priceData.items[i].productPrice) || 0
              : parseFloat(it.productPrice) || 0,
        }));

        if (mergedItems.some((item) => !item.productPrice || item.productPrice <= 0)) {
          return { success: false, error: 'Prix produit invalide pour un ou plusieurs articles' };
        }

        let totalProductPrice = 0;
        let totalCommission = 0;
        mergedItems.forEach((item) => {
          const p = parseFloat(item.productPrice) || 0;
          totalProductPrice += p;
          totalCommission += p * rate;
        });

        const delivery =
          priceData.deliveryFee != null && priceData.deliveryFee !== ''
            ? parseFloat(priceData.deliveryFee)
            : priceData.totalWeight != null && priceData.totalWeight !== ''
              ? parseFloat(priceData.totalWeight) * 2 + 35
              : 35;

        const totalAmount = totalProductPrice + totalCommission + delivery;

        updates = {
          ...updates,
          items: mergedItems,
          totalProductPrice,
          totalCommission,
          totalDeliveryFee: delivery,
          totalAmount,
          finalAmount: totalAmount,
          paymentStatus: nextPaymentStatus,
        };
      } else {
        const productPrice = parseFloat(priceData.productPrice);
        if (!productPrice || productPrice <= 0 || Number.isNaN(productPrice)) {
          return { success: false, error: 'Prix produit invalide' };
        }

        const commission = productPrice * rate;
        const delivery =
          priceData.deliveryFee != null && priceData.deliveryFee !== ''
            ? parseFloat(priceData.deliveryFee)
            : priceData.weight != null && priceData.weight !== ''
              ? parseFloat(priceData.weight) * 2 + 35
              : 35;

        const totalAmount = productPrice + commission + delivery;

        updates = {
          ...updates,
          productPrice,
          commission,
          deliveryFee: delivery,
          totalAmount,
          finalAmount: totalAmount,
          paymentStatus: nextPaymentStatus,
        };
      }

      await updateDoc(ref, updates);
      const fresh = await getDoc(ref);
      return {
        success: true,
        order: { id: fresh.id, _id: fresh.id, ...fresh.data() },
      };
    } catch (error) {
      console.error('updateOrderPrice:', error);
      return {
        success: false,
        error: error.message || 'Erreur lors de la mise à jour du prix',
      };
    }
  },

  /**
   * Expédier une commande (agent/admin) : génère un trackingId, met à jour statuts + adresse.
   */
  shipOrder: async (orderId, deliveryAddress) => {
    try {
      if (!firebaseDb) {
        return { success: false, error: 'Firestore non configuré' };
      }
      const ref = doc(firebaseDb, 'orders', orderId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        return { success: false, error: 'Commande introuvable' };
      }
      const order = snap.data();

      const isCod = order.paymentMethod === 'cash_on_delivery';
      const canShip =
        order.paymentStatus === 'Payé' ||
        (isCod && order.paymentStatus === 'À la livraison');
      if (!canShip) {
        return {
          success: false,
          error: "La commande doit être payée avant d'être expédiée (sauf paiement à la livraison)",
        };
      }
      if (order.trackingId) {
        return { success: false, error: 'Cette commande a déjà un tracking ID' };
      }

      const trackingId = generateTrackingId();
      const updates = {
        trackingId,
        status: 'Expédiée',
        deliveryStatus: 'En transit',
        updatedAt: serverTimestamp(),
      };
      if (deliveryAddress != null && deliveryAddress !== '') {
        updates.deliveryAddress = deliveryAddress;
      }

      await updateDoc(ref, updates);

      const clientUid = order.userUid || order.userId;
      if (clientUid) {
        try {
          await addDoc(collection(firebaseDb, 'users', clientUid, 'notifications'), {
            title: 'Commande expédiée',
            body: `Votre commande a été expédiée. Tracking ID: ${trackingId}`,
            read: false,
            type: 'order_shipped',
            orderId: String(orderId),
            createdAt: serverTimestamp(),
          });
        } catch (notifyErr) {
          console.warn('Notification expédition:', notifyErr);
        }
      }

      const fresh = await getDoc(ref);
      return {
        success: true,
        trackingId,
        order: { id: fresh.id, _id: fresh.id, ...fresh.data() },
      };
    } catch (error) {
      console.error('shipOrder:', error);
      return {
        success: false,
        error: error.message || "Erreur lors de l'expédition",
      };
    }
  },

  /**
   * Mettre à jour le statut de livraison (pour agent/admin)
   */
  updateDeliveryStatus: async (orderId, deliveryStatus) => {
    return orderService.updateOrderStatus(orderId, { deliveryStatus });
  },

  /**
   * Obtenir les détails de tracking d'une commande
   */
  getTrackingDetails: async (orderId) => {
    const res = await orderService.getOrderById(orderId);
    if (!res.success) return res;
    return { success: true, order: res.order, trackingInfo: { trackingId: res.order.trackingId } };
  },
};

